import * as React from 'react';
import { Button, DropdownButton, MenuItem, OverlayTrigger, ToggleButton, ToggleButtonGroup, Tooltip } from 'react-bootstrap';
import { DsArea, OrganType, WageAndTravelCategory } from '@nkr/enums/index';
import { IWatrRequestCounts } from '@nkr/models/index';
import { WatrNavItemWrapper, WatrNavWrapper } from '../styles';
import { isAdvancedDsArea, isAdvReimburseEnabledForCenter } from './watr-nav-area';
import DonorShieldReimbursementPolicies from './donor-shield-reimbursement-policies';

interface IProps {
    centerId: number,
    area: DsArea,
    activeTab: WageAndTravelCategory,
    isNkrAdmin: boolean,
    counts: IWatrRequestCounts,
    organTypes: OrganType[],
    isLoading?: boolean,
    canConfirmAll?: boolean,
    onConfirmAll?: () => void,
    onExport: () => void,
    onChangeOrganType: (organTypes: OrganType[]) => void,
    onChaseExport: (organType: OrganType) => void,
    onValidateRouting: () => void,
    onImportTransactionFile: () => void,
    onReportTabChange?: (category: WageAndTravelCategory) => void,
    onStandardCategoryChange?: (category: WageAndTravelCategory) => void
}

const Filters = (props: IProps) => {
    const [pendingTab, setPendingTab] = React.useState<WageAndTravelCategory | null>(null);

    React.useEffect(() => {
        setPendingTab(null);
    }, [props.activeTab]);

    const displayTab = pendingTab != null ? pendingTab : props.activeTab;

    let toDoTotal = props.counts != null ? props.counts.toDoTotal : 0;
    let pendingDocumentsTotal = props.counts != null ? props.counts.pendingDocumentsTotal : 0;
    let pendingPaymentTotal = props.counts != null ? props.counts.pendingPaymentTotal : 0;
    let pendingWireTotal = props.counts != null ? props.counts.pendingWireTotal : 0;
    let paymentErrorTotal = props.counts != null ? props.counts.paymentErrorTotal : 0;
    let pendingLWRVerifyTotal = props.counts != null ? props.counts.pendingLWRVerifyTotal : 0;
    let pendingDERVerifyTotal = props.counts != null ? props.counts.pendingDERVerifyTotal : 0;
    let reimbursedTotal = props.counts != null ? props.counts.reimbursedTotal : 0;
    let notReimbursedTotal = props.counts != null ? props.counts.notReimbursedTotal : 0;
    let pendingSurgeryTotal = props.counts != null ? props.counts.pendingSurgeryTotal : 0;
    let donatedTotal = props.counts != null ? props.counts.donated : 0;
    let pendingRegistrationTotal = props.counts != null ? props.counts.pendingRegistrationTotal : 0;
    let isCenterView = props.centerId > 0;

    let prefix = props.isNkrAdmin ? '/admin/dash/watr/' : '/center/' + props.centerId + '/donors/watr/';

    let tabList = [
        { id: WageAndTravelCategory.ToDo, name: `To-Do (${toDoTotal})`, suffix: '', include: isCenterView, tooltip: isCenterView ? 'Action required by your center (e.g., mark DSD as donated or approve additional LWR weeks)' : null },
        { id: WageAndTravelCategory.PendRegistration, name: `Pend Profile (${pendingRegistrationTotal})`, suffix: 'pend-registration', include: isCenterView, tooltip: isCenterView ? 'The donor started registration but has not finished filling out their profile' : null },
        { id: WageAndTravelCategory.PendingLWRVerify, name: `Verify LWR (${pendingLWRVerifyTotal})`, suffix: 'pend-lwr-verify', include: !isCenterView, tooltip: null },
        { id: WageAndTravelCategory.PendingDERVerify, name: `Verify DER (${pendingDERVerifyTotal})`, suffix: 'pend-der-verify', include: !isCenterView, tooltip: null },
        { id: WageAndTravelCategory.PendingPayment, name: `Pend ACH (${pendingPaymentTotal})`, suffix: 'pend-payment', include: !isCenterView, tooltip: null },
        { id: WageAndTravelCategory.PendingWire, name: `Pend Wire (${pendingWireTotal})`, suffix: 'pend-wire', include: !isCenterView, tooltip: null },
        { id: WageAndTravelCategory.PaymentError, name: `Payment Error (${paymentErrorTotal})`, suffix: 'payment-error', include: !isCenterView, tooltip: null },
        { id: WageAndTravelCategory.PendDocs, name: isCenterView ? `Pend Document (${pendingDocumentsTotal})` : `Pend Reg (${pendingDocumentsTotal})`, suffix: 'pend-doc', include: true, tooltip: isCenterView ? 'Donor started registration but documents are still pending' : null },
        { id: WageAndTravelCategory.PendSurgery, name: `Pend Surgery (${pendingSurgeryTotal})`, suffix: 'pend-surgery', include: true, tooltip: isCenterView ? 'LWR claim approved; pending surgery' : null },
        { id: WageAndTravelCategory.Reimbursed, name: `Reimbursed (${reimbursedTotal})`, suffix: 'reimbursed', include: false, tooltip: null },
        { id: WageAndTravelCategory.NotReimbursed, name: `Not Reimbursed (${notReimbursedTotal})`, suffix: 'not-reimbursed', include: false, tooltip: null },
        { id: WageAndTravelCategory.Donated, name: `Donated (${donatedTotal})`, suffix: 'donated', include: isCenterView, tooltip: isCenterView ? 'Donation has been completed and confirmed' : null },
        { id: WageAndTravelCategory.Search, name: `Search`, suffix: 'search', include: true, tooltip: isCenterView ? 'Search across all donor cases and statuses' : null },
    ]

    let filteredTabList = tabList.filter(x => x.include);

    let reportTabList = [
        { id: WageAndTravelCategory.YearEndReport, name: 'Audit Review', suffix: 'year-end' },
        { id: WageAndTravelCategory.Transactions, name: 'Transactions', suffix: 'transactions' },
    ];

    let standardLandingSuffix = isCenterView ? '' : 'pend-lwr-verify';
    let onAdvancedArea = isAdvancedDsArea(props.area);
    let onReportsArea = props.isNkrAdmin && (
        displayTab == WageAndTravelCategory.YearEndReport
        || displayTab == WageAndTravelCategory.Transactions
    );

    let showAdvancedParentTab = props.isNkrAdmin || isAdvReimburseEnabledForCenter(props.centerId);

    let parentTabs: { id: 'standard' | 'advanced' | 'reports', name: string, href: string, active: boolean }[] = [
        { id: 'standard', name: 'Standard Reimbursement', href: prefix + standardLandingSuffix, active: !onAdvancedArea && !onReportsArea },
    ];
    if (showAdvancedParentTab) {
        parentTabs.push({ id: 'advanced', name: 'Advanced Reimbursement', href: prefix + 'advanced-reimbursement', active: onAdvancedArea && !onReportsArea });
    }
    if (props.isNkrAdmin) {
        parentTabs.push({ id: 'reports', name: 'Reports', href: prefix + 'year-end', active: onReportsArea });
    }

    const organValue = props.organTypes.length == 2 ? -1 : props.organTypes.includes(OrganType.Kidney) ? OrganType.Kidney : OrganType.Liver;

    let organFilter = (
        <>
            <span style={{ fontWeight: 500, marginRight: 8 }}>Organ:</span>
            <div className="btn-group" data-toggle="buttons">
                <ToggleButtonGroup type='radio' name='organs' value={organValue}>
                    <ToggleButton value={-1} onChange={() => props.onChangeOrganType([OrganType.Kidney, OrganType.Liver])}
                        className={props.organTypes.length == 2 ? 'btn-primary btn-md' : 'btn-md'}>All</ToggleButton>
                    <ToggleButton value={OrganType.Kidney} onChange={() => props.onChangeOrganType([OrganType.Kidney])}
                        className={props.organTypes.length == 1 && props.organTypes.includes(OrganType.Kidney) ? 'btn-primary btn-md' : 'btn-md'}>Kidney</ToggleButton>
                    <ToggleButton value={OrganType.Liver} onChange={() => props.onChangeOrganType([OrganType.Liver])}
                        className={props.organTypes.length == 1 && props.organTypes.includes(OrganType.Liver) ? 'btn-primary btn-md' : 'btn-md'}>Liver</ToggleButton>
                </ToggleButtonGroup>
            </div>
        </>
    );

    const softStandardNav = (category: WageAndTravelCategory, e: React.MouseEvent<HTMLElement>) => {
        if (!props.onStandardCategoryChange) {
            return;
        }
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) {
            return;
        }
        e.preventDefault();
        if (category === displayTab) {
            return;
        }
        setPendingTab(category);
        props.onStandardCategoryChange(category);
    };

    const softReportNav = (category: WageAndTravelCategory, e: React.MouseEvent<HTMLElement>) => {
        if (!props.onReportTabChange) {
            return;
        }
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) {
            return;
        }
        e.preventDefault();
        if (category === displayTab) {
            return;
        }
        setPendingTab(category);
        props.onReportTabChange(category);
    };

    const showStandardChildren = !onAdvancedArea && !onReportsArea;
    const showReportChildren = onReportsArea;
    const hasChildren = showStandardChildren || showReportChildren;
    const shellClass = [
        'watr-nav-shell',
        hasChildren ? 'watr-nav-shell--with-children' : null,
        onAdvancedArea ? 'watr-nav-shell--open-bottom' : null,
        !hasChildren && !onAdvancedArea ? 'watr-nav-shell--solo' : null,
    ].filter(Boolean).join(' ');

    return (
        <div style={{ marginBottom: onAdvancedArea ? 0 : 16 }}>
        <WatrNavWrapper fontSize='15px'>
            <div className={shellClass} style={{ minWidth: 0 }}>
                <div className='watr-nav-shell-header'>
                    <ul className='nav nav-pills ms-pills ms-parent-pills' role='navigation' aria-label='Reimbursement area'>
                        {parentTabs.map((tab, index) => (
                            <React.Fragment key={tab.id}>
                                <WatrNavItemWrapper active={tab.active} href={tab.href} aria-current={tab.active ? 'page' : undefined}>
                                    {tab.name}
                                </WatrNavItemWrapper>
                                {index < parentTabs.length - 1 && (
                                    <li role='presentation' className='li-divider'><div className='pill-divider'></div></li>
                                )}
                            </React.Fragment>
                        ))}
                    </ul>
                    <div className='watr-nav-shell-actions' style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                        {organFilter}
                        {props.isNkrAdmin && displayTab == WageAndTravelCategory.PendingPayment && (<>
                            <DropdownButton
                                bsStyle='primary'
                                title='ACH Payment Actions'
                                id='ach-drop-button'
                            >
                                <MenuItem eventKey="1" onClick={props.onValidateRouting}>Validate Bank Info</MenuItem>
                                <MenuItem eventKey="2" onClick={() => props.onChaseExport(OrganType.Kidney)}>1-Export Chase ACH Kidney</MenuItem>
                                <MenuItem eventKey="3" onClick={() => props.onChaseExport(OrganType.Liver)}>2-Export Chase ACH Liver</MenuItem>
                                <MenuItem divider />
                                <MenuItem eventKey="4" onClick={props.onImportTransactionFile}>Import Chase ACH Trxns</MenuItem>
                            </DropdownButton>
                            <Button bsStyle='primary' disabled={!props.canConfirmAll} onClick={props.onConfirmAll}>Confirm All</Button>
                        </>)}
                        {!props.isNkrAdmin && (
                            <DonorShieldReimbursementPolicies show={true} />
                        )}
                        <Button bsStyle='success' onClick={props.onExport}>Export Grid (CSV)</Button>
                    </div>
                </div>

                {showStandardChildren && (
                    <ul className='nav nav-pills ms-pills ms-child-tabs' role='navigation' aria-label='Request category' style={{ opacity: props.isLoading ? 0.72 : 1 }}>
                        {filteredTabList.map((tab, index) => (
                            <React.Fragment key={'tab-' + tab.id}>
                                {tab.tooltip ? (
                                    <OverlayTrigger placement='bottom' overlay={<Tooltip id={`watr-tab-tooltip-${tab.id}`}>{tab.tooltip}</Tooltip>} trigger={['hover', 'focus']}>
                                        <WatrNavItemWrapper active={tab.id == displayTab} href={prefix + tab.suffix} onClick={(e) => softStandardNav(tab.id, e)} aria-current={tab.id == displayTab ? 'page' : undefined}>
                                            {tab.name}
                                        </WatrNavItemWrapper>
                                    </OverlayTrigger>
                                ) : (
                                    <WatrNavItemWrapper active={tab.id == displayTab} href={prefix + tab.suffix} onClick={(e) => softStandardNav(tab.id, e)} aria-current={tab.id == displayTab ? 'page' : undefined}>
                                        {tab.name}
                                    </WatrNavItemWrapper>
                                )}
                                {index < filteredTabList.length - 1 && (
                                    <li role='presentation' className='li-divider'><div className='pill-divider'></div></li>
                                )}
                            </React.Fragment>
                        ))}
                    </ul>
                )}

                {showReportChildren && (
                    <ul className='nav nav-pills ms-pills ms-child-tabs' role='navigation' aria-label='Reports'>
                        {reportTabList.map((tab, index) => (
                            <React.Fragment key={'report-tab-' + tab.id}>
                                <WatrNavItemWrapper
                                    active={tab.id == displayTab}
                                    href={prefix + tab.suffix}
                                    onClick={(e) => softReportNav(tab.id, e)}
                                    aria-current={tab.id == displayTab ? 'page' : undefined}
                                >
                                    {tab.name}
                                </WatrNavItemWrapper>
                                {index < reportTabList.length - 1 && (
                                    <li role='presentation' className='li-divider'><div className='pill-divider'></div></li>
                                )}
                            </React.Fragment>
                        ))}
                    </ul>
                )}
            </div>
        </WatrNavWrapper>
        </div>
    );
}

export default Filters;
